import { Component, Input, ViewEncapsulation } from '@angular/core';
import { RcsMessage, MessageType, Suggestion, SuggestionType } from '../../../../../../_models/messaging/messages/rcs-messages/rcs-message.model';

@Component({
  selector: 'app-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class PreviewComponent {
  @Input() message: RcsMessage | null = null;
  MessageType = MessageType;
  SuggestionType = SuggestionType;

  selectedOS: 'ios' | 'android' = 'ios';

  activeCardIndex = 0;

  onCarouselScroll(event: Event): void {
    const container = event.target as HTMLElement;
    const cardWidth = container.firstElementChild
      ? (container.firstElementChild as HTMLElement).offsetWidth
      : 1;
    this.activeCardIndex = Math.round(container.scrollLeft / cardWidth);
  }
  
  switchOS(os: 'ios' | 'android'): void {
    this.selectedOS = os;
  }
  
  // iOS may show RCS as SMS fallback or in a different format
  isIOSRCSSupported(): boolean {
    // iOS has limited RCS support - may fallback to SMS or show differently
    return false; // iOS doesn't fully support RCS natively
  }
  
  // Android has full native RCS support
  isAndroidRCSSupported(): boolean {
    return true; // Android has full RCS support
  }
  
  // Get platform-specific message rendering
  getPlatformMessageStyle(): string {
    if (this.selectedOS === 'ios') {
      return 'ios-message'; // iOS may show as SMS-like or limited RCS
    } else {
      return 'android-rcs'; // Android shows full RCS features
    }
  }

  getRichCardSuggestions(): Suggestion[] {
    if (!this.message?.richCard) {
      return [];
    }
    if (this.message.suggestions?.length) {
      return this.message.suggestions;
    }
    return this.message.richCard.suggestions || [];
  }

  handleSuggestionClick(suggestion: Suggestion, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    switch (suggestion.type) {
      case SuggestionType.OPEN_URL:
        if (suggestion.url) {
          window.open(suggestion.url, '_blank', 'noopener,noreferrer');
        }
        break;

      case SuggestionType.DIAL_PHONE:
        if (suggestion.phone) {
          window.location.href = `tel:${suggestion.phone}`;
        }
        break;

      case SuggestionType.VIEW_LOCATION:
        if (suggestion.latitude && suggestion.longitude) {
          const url = `https://www.google.com/maps?q=${suggestion.latitude},${suggestion.longitude}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        } else if (suggestion.address) {
          const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(suggestion.address)}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;

      case SuggestionType.CALENDAR_EVENT:
        if (suggestion.startTime && suggestion.endTime) {
          this.createCalendarEvent(suggestion);
        }
        break;

      case SuggestionType.REPLY:
        // For reply, we can show an alert or console log
        console.log('Reply clicked:', suggestion.displayText);
        alert(`Reply: ${suggestion.displayText}`);
        break;

      default:
        console.log('Suggestion clicked:', suggestion);
    }
  }

  private createCalendarEvent(suggestion: Suggestion): void {
    if (!suggestion.startTime || !suggestion.endTime) return;

    const startDate = new Date(suggestion.startTime);
    const endDate = new Date(suggestion.endTime);

    // Format dates for Google Calendar
    const formatDate = (date: Date): string => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = encodeURIComponent(suggestion.meetingTitle || 'Event');
    const details = encodeURIComponent(suggestion.meetingDescription || '');
    const start = formatDate(startDate);
    const end = formatDate(endDate);

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${start}/${end}`;
    window.open(googleCalendarUrl, '_blank', 'noopener,noreferrer');
  }

  getSuggestionTooltip(suggestion: Suggestion): string {
    switch (suggestion.type) {
      case SuggestionType.OPEN_URL:
        return suggestion.url ? `Open: ${suggestion.url}` : 'Open URL';
      case SuggestionType.DIAL_PHONE:
        return suggestion.phone ? `Call: ${suggestion.phone}` : 'Dial Phone';
      case SuggestionType.VIEW_LOCATION:
        return suggestion.address || (suggestion.latitude && suggestion.longitude) 
          ? 'View Location' : 'View Location';
      case SuggestionType.CALENDAR_EVENT:
        return suggestion.meetingTitle ? `Add to Calendar: ${suggestion.meetingTitle}` : 'Add to Calendar';
      case SuggestionType.REPLY:
        return 'Reply';
      default:
        return suggestion.displayText;
    }
  }

  handleImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
  }
}
